import { Component, OnInit } from '@angular/core';
import {
	AbstractModal,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType
} from '@cv-invision/core';
// Services
import { ClientIdentifierListService } from '../service/client-identifier-list.service';
import { Subject, takeUntil } from 'rxjs';
import { ADD_EDIT_CLIENT_IDENTIFIER, ClientIdentifierModel, CustomField } from '../model/client-identifier.model';
import { MessageService } from 'primeng/api';

@Component({
	selector: 'cv-add-edit-client-identifier',
	styleUrls: ['./add-edit-client-identifier.component.scss'],
	templateUrl: './add-edit-client-identifier.component.html',
	providers: [MessageService]
})
export class AddEditClientIdentifier extends AbstractModal implements OnInit {
	public model: ClientIdentifierModel;
	public customFields: any = [];
	public saving: boolean;

	private isEditing: boolean;
	private integrationName: string;
	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		private service: ClientIdentifierListService,
		protected broadcastService: BroadcastService,
		private messageService: MessageService,
	) { 
		super();
	}

	ngOnInit(): void {
		this.model           = this.data.clientIdentifier ? {...this.data.clientIdentifier} : new ClientIdentifierModel();
		this.integrationName = this.data.integrationName;
		this.isEditing       = this.data.clientIdentifier !== null;
		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};

		const saveButton: ModalButtonModel = {
			name: 'save',
			text: 'Save',
			class: 'btn-sm cv-btn-blue ',
			type: ModalButtonType.ACTION,
			disabled: (): boolean => !this.setIsFormValid() || this.saving,
			action: () => this.onSave(),
			loading: (): boolean => this.saving,
			loadingText: 'Saving'
		};

		this.buttons.push(cancelButton);
		this.buttons.push(saveButton);
	}

	/**
	 * Determines if the form is valid.
	 */
	setIsFormValid(): boolean {
		const { identifier, integrationClientId } = this.model;
		return (
			this.isNonEmpty(identifier) &&
			this.isNonEmpty(integrationClientId) &&
			this.areCustomFieldsValid()
		);
	}

	/**
	 * Checks whether a string is non-empty after trimming.
	 */
	private isNonEmpty(value: string): boolean {
		return value && value.trim().length > 0;
	}

	/**
	 * Validates custom fields.
	 */
	private areCustomFieldsValid(): boolean {
		const { customFields } = this.model;
		if (!customFields || customFields.length === 0) return false;

		return customFields.every(field =>
			this.isNonEmpty(field.field) && this.isNonEmpty(field.value)
		);
	}

	/**
	 * Saves the client identifier.
	 * If editing, updates the existing identifier; otherwise, creates a new one.
	 * Displays a success message upon completion.
	 * Handles errors by displaying an error message.
	 * @returns {void}
	 */
	onSave(): void {
		this.saving = true;
		const operation = this.isEditing
			? this.service.updateClientIdentifier(this.integrationName, this.model)
			: this.service.createClientIdentifier(this.integrationName, this.model);

		operation
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: () => {
					this.broadcastService.broadcast({
						name: ADD_EDIT_CLIENT_IDENTIFIER,
						event: { isEditing: this.isEditing }
					});
					super.onAcceptSuccess({ success: true });
					this.saving = false;
				},
				error: error => {
					this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error });
					this.saving = false;
				}
			});
	}

	/**
	 * Edit client identifier
	 * @param clientIdentifierId 
	 */
	public deleteCustomField(index: number): void {
		this.model.customFields = this.model.customFields.slice(0, index).concat(this.model.customFields.slice(index + 1));
	}

	/**
	 * Add a custom Field
	 */
	public addCustomField(): void {
		this.model.customFields.push(new CustomField())
	}

	onDismiss(): void {
		super.onCancel();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
